"use client";

import messages from "@/i18n/messages/en.json";

import { Button, Input, Skeleton } from "@artha/ui";
import { Check, Pencil } from "lucide-react";
import { isValidWhatsAppNumber } from "@artha/utils";

import type { CandidateContactVerificationStepProps } from "../../personalization.types";

import {
  PERSONALIZATION_FIELD_CLASS_NAME,
  PERSONALIZATION_PRIMARY_ACTION_CLASS_NAME,
  PERSONALIZATION_RESEND_ACTION_CLASS_NAME,
} from "../../personalization.constants";
import {
  PHONE_CODE_PATTERN,
  PHONE_CODE_MAX_LENGTH,
  CONTACT_EDIT_BUTTON_CLASS_NAME,
  CONTACT_EDIT_ICON_CLASS_NAME,
} from "./verification.constants";

import { useCandidateContactVerification } from "../../hooks/useCandidateContactVerification";
export function CandidateContactVerificationStep(
  props: CandidateContactVerificationStepProps,
) {
  const { onEditContact, onComplete } = props;
  const {
    error,
    status,
    action,
    prepare,
    emailSending,
    emailWait,
    sendEmail,
    phone,
    sent,
    phoneWait,
    sendPhone,
    verify,
    code,
    setCode,
  } = useCandidateContactVerification(props);
  return (
    <div className="space-y-4">
      {error && (
        <p
          role="alert"
          className="rounded-lg bg-[#FEF3F2] p-3 text-sm text-[#B42318]"
        >
          {error}
        </p>
      )}
      {!status ? (
        action === "preparing" ? (
          <div
            role="status"
            aria-label={messages.talentNetworkUi.preparing_contact_verification}
          >
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="mt-4 h-44 w-full rounded-xl" />
          </div>
        ) : (
          <Button className="w-full" onClick={() => void prepare()}>
            {messages.talentNetworkUi.try_again}
          </Button>
        )
      ) : (
        <>
          <section
            aria-label={messages.talentNetworkUi.email}
            className="space-y-2 text-sm text-[#475467]"
          >
            <h3 className="font-semibold text-[#344054]">
              {messages.talentNetworkUi.email}
            </h3>
            <p className="break-words">
              {status.emailVerified
                ? messages.talentNetworkUi.email_verified
                : emailSending
                  ? messages.talentNetworkUi.sending_verification_link_to
                  : status.emailLinkSent
                    ? messages.talentNetworkUi.verification_link_sent_to
                    : messages.talentNetworkUi.verification_link_not_sent_to}
              <a
                className="break-all underline underline-offset-2"
                href={`mailto:${status.editableProfile.email}`}
              >
                {status.editableProfile.email}
              </a>
              <button
                type="button"
                aria-label={messages.talentNetworkUi.edit_email}
                className={CONTACT_EDIT_BUTTON_CLASS_NAME}
                disabled={action === "verifying"}
                onClick={() => onEditContact("email")}
              >
                <Pencil
                  aria-hidden="true"
                  className={CONTACT_EDIT_ICON_CLASS_NAME}
                />
              </button>
            </p>
            {status.emailVerified ? (
              <p className="flex items-center gap-1 text-green-600">
                <Check aria-hidden="true" className="size-4" />
                {messages.talentNetworkUi.verified}
              </p>
            ) : (
              <>
                <p>
                  {messages.talentNetworkUi.you_can_verify_your_email_later}
                </p>
                {status.emailLinkError && (
                  <p role="alert" className="text-[#B42318]">
                    {status.emailLinkError}
                  </p>
                )}
                <button
                  type="button"
                  aria-label={
                    emailSending
                      ? messages.talentNetworkUi.sending_email_link
                      : emailWait
                        ? `Resend email link (${emailWait}s)`
                        : messages.talentNetworkUi.resend_email_link
                  }
                  className={PERSONALIZATION_RESEND_ACTION_CLASS_NAME}
                  disabled={
                    emailSending || emailWait > 0 || action === "verifying"
                  }
                  onClick={() => void sendEmail(status.externalId)}
                >
                  {emailSending
                    ? messages.talentNetworkUi.sending
                    : emailWait
                      ? `Resend (${emailWait}s)`
                      : messages.talentNetworkUi.resend}
                </button>
              </>
            )}
          </section>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              if (status.phoneVerified) onComplete();
              else void verify();
            }}
          >
            <section
              aria-label={messages.talentNetworkUi.phone}
              className="space-y-2 text-sm text-[#475467]"
            >
              <h3 className="font-semibold text-[#344054]">
                {messages.talentNetworkUi.phone}
              </h3>
              <p
                className="break-words"
                role={action === "sending" ? "status" : undefined}
              >
                {status.phoneVerified
                  ? messages.talentNetworkUi.phone_verified
                  : action === "sending"
                    ? messages.talentNetworkUi.sending_code_to
                    : sent
                      ? messages.talentNetworkUi.enter_the_code_sent_to
                      : messages.talentNetworkUi.phone_number}
                <span className="whitespace-nowrap">
                  {phone || "Not provided"}
                </span>
                <button
                  type="button"
                  aria-label={messages.talentNetworkUi.edit_phone_number}
                  className={CONTACT_EDIT_BUTTON_CLASS_NAME}
                  disabled={action === "verifying"}
                  onClick={() => onEditContact("phone")}
                >
                  <Pencil
                    aria-hidden="true"
                    className={CONTACT_EDIT_ICON_CLASS_NAME}
                  />
                </button>
              </p>
              {status.phoneVerified && (
                <p className="flex items-center gap-1 text-green-600">
                  <Check aria-hidden="true" className="size-4" />
                  {messages.talentNetworkUi.verified}
                </p>
              )}
            </section>
            {!status.phoneVerified && (
              <>
                <label className="block space-y-2 text-sm font-semibold text-[#344054]">
                  {messages.talentNetworkUi.verification_code}
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    className={PERSONALIZATION_FIELD_CLASS_NAME}
                    value={code}
                    disabled={Boolean(action)}
                    maxLength={PHONE_CODE_MAX_LENGTH}
                    onChange={(event) =>
                      setCode(
                        event.target.value
                          .replace(/\D/g, "")
                          .slice(0, PHONE_CODE_MAX_LENGTH),
                      )
                    }
                  />
                </label>
                <button
                  type="button"
                  aria-label={
                    action === "sending"
                      ? messages.talentNetworkUi.sending_phone_code
                      : phoneWait
                        ? `Resend phone code (${phoneWait}s)`
                        : messages.talentNetworkUi.resend_phone_code
                  }
                  className={PERSONALIZATION_RESEND_ACTION_CLASS_NAME}
                  disabled={
                    Boolean(action) ||
                    phoneWait > 0 ||
                    !isValidWhatsAppNumber(phone)
                  }
                  onClick={() => void sendPhone(status.externalId, phone)}
                >
                  {action === "sending"
                    ? messages.talentNetworkUi.sending
                    : phoneWait
                      ? `Resend (${phoneWait}s)`
                      : messages.talentNetworkUi.resend}
                </button>
              </>
            )}
            <Button
              type="submit"
              className={PERSONALIZATION_PRIMARY_ACTION_CLASS_NAME}
              disabled={
                Boolean(action) ||
                (!status.phoneVerified &&
                  (!isValidWhatsAppNumber(phone) ||
                    !PHONE_CODE_PATTERN.test(code)))
              }
            >
              {action === "verifying"
                ? messages.talentNetworkUi.verifying
                : status.phoneVerified
                  ? messages.talentNetworkUi.continue
                  : messages.talentNetworkUi.verify_phone_and_continue}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
